import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { resetAllSearch } from 'actions/search'
import { setIsDrawerVisible } from 'actions/drawer'
import {
  navigatorIsFullscreen,
  setSelectedIdWithDrawer,
} from 'actions/navigator'
import { resetCollection } from 'actions/collection'
import { clone, compose, map, reject } from 'ramda'
import strings from 'utils/strings'
import Search from './Search'
import CollectionReview from './CollectionReview'
import ResolveConflicts from './ResolveConflicts'
import ReviewFeatures from './ReviewFeatures'
import Commit from './Commit'
import FeatureCollectionsHeader from './FeatureCollectionsHeader'

const FeatureCollections = ({ serialNumber }) => {
  const dispatch = useDispatch()

  const [step, setStep] = useState(0)
  const [selectedFeatures, setSelectedFeatures] = useState([])
  const [mappedIoSwitches, setMappedIoSwitches] = useState({})
  const [keepOrRemoveSwitches, setKeepOrRemoveSwitches] = useState([])
  const [onGoingSet, setOnGoingSet] = useState(null)

  const changeStep = useCallback(
    (nextStep) => {
      setStep(nextStep)
    },
    [setStep]
  )

  const reset = useCallback(() => {
    setStep(0)
    setSelectedFeatures([])
    setMappedIoSwitches({})
    setOnGoingSet(null)
    dispatch(resetAllSearch('features'))
    dispatch(resetCollection())
  }, [dispatch])

  const handleExit = useCallback(() => {
    reset()
    dispatch(setIsDrawerVisible(false))
    dispatch(setSelectedIdWithDrawer(null))
    // have to reset drawer width because width is in navigator
    // open/close is in drawer - drawer width should be width drawer
    dispatch(navigatorIsFullscreen(false))
  }, [reset, dispatch])

  const handleRemoveFeature = (ids) => {
    const removedIds = Array.isArray(ids) ? ids : [ids]

    const updateSet = map((feature) => {
      const isFeatureInRemovedList = removedIds.includes(feature.id)

      if (isFeatureInRemovedList) {
        if (feature.isMarkedForUpdate) {
          return {
            ...feature.previousSnapshot,
            isMarkedForDeletion: true,
          }
        }
        return {
          ...feature,
          isMarkedForDeletion: true,
        }
      }

      return feature
    })

    const shouldRemoveFromList = (f) =>
      f.isMarkedForDeletion && f.isMarkedForAddition && !f.isMarkedForUpdate

    const updatedSet = compose(updateSet)(clone(onGoingSet))

    const createFeaturesToRemovelist = (list) =>
      list.reduce((acc, curr) => {
        if (curr.isMarkedForDeletion) return [...acc, curr.id]
        return acc
      }, [])

    const featuresMarkedForRemoval = createFeaturesToRemovelist(updatedSet)

    const createSignalsToRemoveList = (list) =>
      Object.entries(list).reduce((acc, [key, value]) => {
        if (featuresMarkedForRemoval.includes(value.featureId))
          return [...acc, key]
        return acc
      }, [])

    const mappedSwitches = clone(mappedIoSwitches)
    const keepOrRemoveSwitchesClone = clone(keepOrRemoveSwitches)

    const signalIdsToRemove = [
      ...createSignalsToRemoveList(mappedSwitches),
      ...createSignalsToRemoveList(keepOrRemoveSwitchesClone),
    ]

    signalIdsToRemove.forEach((signal) => {
      delete mappedSwitches[signal]
      delete keepOrRemoveSwitchesClone[signal]
    })

    const nextSet = compose(reject(shouldRemoveFromList))(updatedSet)

    setOnGoingSet(nextSet)
    setMappedIoSwitches(mappedSwitches)
    setKeepOrRemoveSwitches(keepOrRemoveSwitchesClone)
  }

  const views = [
    <Search
      serialNumber={serialNumber}
      reset={reset}
      changeStep={changeStep}
      key="search"
    />,
    <CollectionReview
      serialNumber={serialNumber}
      changeStep={changeStep}
      reset={reset}
      setSelectedFeatures={setSelectedFeatures}
      key="collection-review"
    />,
    <ResolveConflicts
      serialNumber={serialNumber}
      changeStep={changeStep}
      selectedFeatures={selectedFeatures}
      setIoSwitches={setMappedIoSwitches}
      setReviewFeatures={setOnGoingSet}
      onGoingSet={onGoingSet}
      disabledMessage={strings.vehicle_collection_conflicts_must_be_resolved}
      key="resolve-conflicts"
    />,
    <ReviewFeatures
      serialNumber={serialNumber}
      reset={reset}
      changeStep={changeStep}
      onGoingSet={onGoingSet}
      mappedIoSwitches={mappedIoSwitches}
      setKeepOrRemoveSwitches={setKeepOrRemoveSwitches}
      onRemoveFeatures={handleRemoveFeature}
      key="review-features"
    />,
    <Commit
      serialNumber={serialNumber}
      changeStep={changeStep}
      reset={reset}
      exit={handleExit}
      onGoingSet={onGoingSet}
      mappedIoSwitches={mappedIoSwitches}
      keepOrRemoveSwitches={keepOrRemoveSwitches}
      key="commit"
    />,
  ]

  return (
    <>
      <FeatureCollectionsHeader
        serialNumber={serialNumber}
        step={step}
        exit={handleExit}
      />
      <div>{views[step]}</div>
    </>
  )
}

FeatureCollections.propTypes = {
  serialNumber: PropTypes.string,
}

export default FeatureCollections
